import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

/**
 * Last path segment of a URL, without the query string or fragment a
 * cache-busted template URL carries — those are not part of the name the
 * browser should save the download under.
 */
function fileNameFromUrl(url: string): string {
  const [path = ''] = url.split(/[?#]/);

  return path.split('/').pop() || 'file';
}

/**
 * Fetches a file from the given URL and returns it as a File object.
 *
 * @param {string} url - The URL to fetch the file from.
 * @param {string} type - The MIME type of the file.
 * @returns {Promise<File>} - A promise that resolves to a File object.
 * @throws {Error} When the response is not successful, so callers see the
 * HTTP status instead of a generic parsing failure further down the chain.
 */
export const getTemplateFile = async (
  url: string,
  type = 'text/csv',
): Promise<File> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch template file at ${url}: ${response.status} ${response.statusText}`,
    );
  }

  const blob = await response.blob();

  return new File([blob], fileNameFromUrl(url), { type });
};

export const useTemplateFile = (
  path: string,
  options?: Omit<UseQueryOptions<File>, 'enabled' | 'queryFn' | 'queryKey'>,
  mime = 'text/csv',
) =>
  useQuery<File>({
    enabled: Boolean(path) && Boolean(mime),
    queryFn: async () => getTemplateFile(path, mime),
    queryKey: ['templateFile', path, mime],
    staleTime: Infinity, // cache forever, since template files rarely change,
    throwOnError: false,
    ...options,
  });
